import { useState } from 'react';
import { Box, Button, IconButton, Stack, Tooltip } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import { type GridColDef } from '@mui/x-data-grid';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import dayjs from 'dayjs';
import { PageHeader } from '../../../shared/components/PageHeader';
import { DataTable, type DataTableQuery } from '../../../shared/components/DataTable';
import { GRID_PAGE_SIZE, usePersistedGridState } from '../../../shared/grid/usePersistedGridState';
import { ColaboradorSelect } from '../../../shared/components/ColaboradorSelect';
import { ConfirmDialog } from '../../../shared/components/ConfirmDialog';
import { ErrorState } from '../../../shared/components/ErrorState';
import { useToast } from '../../../shared/stores/toast.store';
import { describeApiError } from '../../../shared/api/errors';
import { createEmployeeFeriasApi } from '../ferias/api';
import { feriasGlobalApi } from './api';
import { FeriaGlobalFormDialog } from './FeriaGlobalFormDialog';
import type { FeriaGlobalListItem } from './types';

const DEFAULT_QUERY: DataTableQuery = {
  page: 1,
  pageSize: GRID_PAGE_SIZE,
  sortBy: 'dataInicio',
  sortOrder: 'desc',
};

function formatDate(iso: string): string {
  const d = dayjs(iso);
  return d.isValid() ? d.format('DD/MM/YYYY') : iso;
}

/**
 * Visão global de Férias (administrativo) — grid de todos os colaboradores com
 * filtro por colaborador + CRUD. Espelha a página Faltas global. A listagem usa
 * `feriasGlobalApi` (mock até card [6xpYsgzK]); as mutações reusam o
 * sub-recurso aninhado `/employees/:id/ferias`.
 */
export function FeriasGlobalPage(): JSX.Element {
  const { t } = useTranslation();
  const toast = useToast();
  const qc = useQueryClient();
  const {
    query,
    setQuery,
    filters,
    patchFilters,
    clear,
  } = usePersistedGridState('ferias-global', DEFAULT_QUERY, {
    colaboradorCodigo: null as number | null,
  });
  const colabFiltro = filters.colaboradorCodigo;
  const setColabFiltro = (id: number | null) => patchFilters({ colaboradorCodigo: id });
  const [editing, setEditing] = useState<FeriaGlobalListItem | null | 'new'>(null);
  const [pendingDelete, setPendingDelete] = useState<FeriaGlobalListItem | null>(null);

  const listQuery = useQuery({
    queryKey: ['ferias', 'global', query, colabFiltro],
    queryFn: () => feriasGlobalApi.list(query, { colaboradorCodigo: colabFiltro }),
    placeholderData: (previous) => previous,
  });

  const deleteMut = useMutation({
    mutationFn: (row: FeriaGlobalListItem) =>
      createEmployeeFeriasApi(row.employeeId).remove(row.dataInicio),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['ferias', 'global'] });
      toast.success(t('features.colaboradores.ferias.toasts.deleted'));
      setPendingDelete(null);
    },
    onError: (err) => toast.error(describeApiError(err)),
  });

  const columns: GridColDef<FeriaGlobalListItem>[] = [
    {
      field: 'colaboradorNome',
      headerName: t('features.colaboradores.feriasGlobal.columns.colaborador'),
      flex: 1,
      minWidth: 200,
    },
    {
      field: 'dataInicio',
      headerName: t('features.colaboradores.ferias.columns.dataInicio'),
      width: 130,
      valueFormatter: (value: string) => formatDate(value),
    },
    {
      field: 'dataFim',
      headerName: t('features.colaboradores.ferias.columns.dataFim'),
      width: 130,
      valueFormatter: (value: string) => formatDate(value),
    },
    {
      field: 'observacao',
      headerName: t('features.colaboradores.ferias.columns.observacao'),
      flex: 1,
      minWidth: 200,
      sortable: false,
    },
    {
      field: 'actions',
      headerName: '',
      width: 100,
      sortable: false,
      filterable: false,
      align: 'right',
      headerAlign: 'right',
      renderCell: (params) => (
        <Stack direction="row" spacing={0.5}>
          <Tooltip title={t('common.edit')}>
            <IconButton
              size="small"
              aria-label={t('features.colaboradores.feriasGlobal.actions.edit', {
                nome: params.row.colaboradorNome,
                data: formatDate(params.row.dataInicio),
              })}
              onClick={() => setEditing(params.row)}
            >
              <EditIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title={t('common.delete')}>
            <IconButton
              size="small"
              color="error"
              aria-label={t('features.colaboradores.feriasGlobal.actions.delete', {
                nome: params.row.colaboradorNome,
                data: formatDate(params.row.dataInicio),
              })}
              onClick={() => setPendingDelete(params.row)}
            >
              <DeleteIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Stack>
      ),
    },
  ];

  return (
    <Box>
      <PageHeader
        title={t('features.colaboradores.feriasGlobal.title')}
        subtitle={t('features.colaboradores.feriasGlobal.subtitle')}
        actions={
          <Button variant="contained" startIcon={<AddIcon />} onClick={() => setEditing('new')}>
            {t('features.colaboradores.feriasGlobal.actions.new')}
          </Button>
        }
      />
      <Stack spacing={2}>
        {/* Erro na carga inicial (ex.: upstream 500 → 502): mostra o erro DENTRO
            da página — com o cabeçalho preservado e retry — em vez de substituir
            a tela inteira (que parecia "não carregar"). */}
        {listQuery.isLoadingError ? (
          <ErrorState
            description={describeApiError(listQuery.error)}
            onRetry={() => listQuery.refetch()}
          />
        ) : (
          <DataTable<FeriaGlobalListItem>
            rows={listQuery.data?.data ?? []}
            columns={columns}
            total={listQuery.data?.meta.total ?? 0}
            loading={listQuery.isLoading || listQuery.isFetching}
            query={query}
            onQueryChange={setQuery}
            onClear={clear}
            searchPlaceholder={t('features.colaboradores.feriasGlobal.searchPlaceholder')}
            filters={
              <Box sx={{ minWidth: 220, flex: '1 1 260px' }}>
                <ColaboradorSelect
                  value={colabFiltro}
                  onChange={(id) => setColabFiltro(id)}
                  label={t('features.colaboradores.feriasGlobal.filters.colaborador')}
                  placeholder={t('features.colaboradores.feriasGlobal.filters.colaboradorTodos')}
                />
              </Box>
            }
          />
        )}
      </Stack>

      <FeriaGlobalFormDialog
        open={editing !== null}
        feria={editing === 'new' ? null : editing}
        onClose={() => setEditing(null)}
      />

      <ConfirmDialog
        open={pendingDelete !== null}
        title={t('features.colaboradores.ferias.deleteConfirm.title')}
        description={t('features.colaboradores.feriasGlobal.deleteConfirm.description', {
          nome: pendingDelete?.colaboradorNome ?? '',
          inicio: pendingDelete ? formatDate(pendingDelete.dataInicio) : '',
          fim: pendingDelete ? formatDate(pendingDelete.dataFim) : '',
        })}
        confirmLabel={t('common.delete')}
        loading={deleteMut.isPending}
        onConfirm={() => {
          if (pendingDelete) deleteMut.mutate(pendingDelete);
        }}
        onCancel={() => setPendingDelete(null)}
      />
    </Box>
  );
}
